import { Autocomplete, AutocompleteItem } from '@/components/ui';
import type { Key } from 'react';
import { useEffect, useState } from 'react';

export interface CampusAutocompleteItem {
  id: string;
  name: string;
}

export interface CampusAutocompleteProps {
  /** List of campuses to choose from */
  items: CampusAutocompleteItem[];
  /** Selected campus id, or empty string for none */
  value: string;
  /** Called when selection changes (id or '') */
  onChange: (id: string) => void;
  placeholder?: string;
  label?: React.ReactNode;
  className?: string;
  'aria-label'?: string;
  disabled?: boolean;
  /** When true, show a "Clear selection" link when a value is set. Default true. */
  allowClear?: boolean;
  /** When true, render the listbox in a portal so it is not clipped by overflow (e.g. inside a modal). Default false. */
  portal?: boolean;
}

/**
 * Reusable campus autocomplete: type to filter, pick one, optional clear.
 * Use with any list of { id, name } (e.g. from API or store).
 */
export function CampusAutocomplete({
  items,
  value,
  onChange,
  placeholder = 'All campuses',
  label,
  className,
  'aria-label': ariaLabel,
  disabled,
  allowClear = true,
  portal = false,
}: CampusAutocompleteProps) {
  const selectedName =
    value && items.length
      ? (items.find((c) => c.id === value)?.name ?? '')
      : '';

  const [inputValue, setInputValue] = useState(selectedName);

  useEffect(() => {
    setInputValue(selectedName);
  }, [selectedName]);

  const handleSelectionChange = (key: Key | null) => {
    const id = key ? String(key) : '';
    const item = id ? items.find((c) => c.id === id) : null;
    setInputValue(item?.name ?? '');
    onChange(id);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
  };

  return (
    <div className={className}>
      <Autocomplete
        label={label}
        placeholder={placeholder}
        items={items}
        selectedKey={value || null}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSelectionChange={handleSelectionChange}
        disabled={disabled}
        aria-label={ariaLabel ?? 'Select campus'}
        listboxProps={{
          emptyContent:
            inputValue.trim() === ''
              ? 'Type to search campuses'
              : 'No matching campus',
          portal,
        }}
      >
        {(item) => (
          <AutocompleteItem key={item.id} textValue={item.name}>
            {item.name}
          </AutocompleteItem>
        )}
      </Autocomplete>
      {allowClear && value && (
        <button
          type='button'
          className='mt-1.5 text-xs text-primary hover:underline'
          onClick={handleClear}
        >
          Clear selection
        </button>
      )}
    </div>
  );
}
